<template>
    <card class="stretch-layout" xs-tile sm-tile>
		<div v-if="pageStyle && pageStyle.content" class="p-16">
			<user-content :text="pageStyle.content" />
		</div>
		<client-only>
			<event-map :event="eventStore.event" :participation-classes="[participationStore.participation.participationClass]" :checkpoints="checkpoints"
					   :tracks="focusedTrack ? [focusedTrack] : tracks"
					   :focus-elements="focusedTrack"
					   :track-spots="trackSpots"
					   with-query-filter
					   with-types-filter
					   downloadable @track-download="downloadTrack" class="stretch-layout">

				<template v-if="tracks.length" #action_controls>
					<btn small icon class="w-36 min-w-36 h-36 min-h-36" @click="downloadTrack()" :title="$t('action.download')">
						<icon :icon="icons.download"/>
					</btn>
				</template>

				<template #default="{inPrintMode}">
					<template v-if="!inPrintMode">
						<template v-for="chartMarkerTrack in chartTracks" :key="`chart-marker-${chartMarkerTrack.id}`">
							<map-marker v-if="(!focusedTrack || focusedTrack.id === chartMarkerTrack.id) && selectedChartLatLngPerTrack[chartMarkerTrack.id]"
										:lat-lng="selectedChartLatLngPerTrack[chartMarkerTrack.id]"
										:height="16" :width="16"
										text=" "
										pulse
										no-arrow
										border-color="#fff"
										:color="chartMarkerTrack.color"/>
						</template>
					</template>
				</template>

				<template v-if="chartTracks.length" #footer>
					<div class="bg-white">
						<track-chart v-model:focus-track="focusedTrack" v-model:hover-distance="selectedChartDistanceFromStart" :tracks="chartTracks" :height="200"/>
					</div>
				</template>

			</event-map>

			<lazy-track-download-form-modal v-if="tracks.length"
											v-model="showTrackDownloadFormModal"
											:tracks="tracks"
											:has-control-spots="trackSpots.some(spot => spot.type === TrackSpotType.Control)"
											:has-navigation-spots="trackSpots.some(spot => spot.type === TrackSpotType.Navigation)"
											:has-result-spots="trackSpots.some(spot => spot.type === TrackSpotType.Result)"
											:selected="selectedDownloadTrackId"
											:event-id="eventStore.event.id"/>
		</client-only>

    </card>
</template>

<script setup lang="ts">

import {EventPageType, TrackSpotType} from "@spoferan/spoferan-ts-core";
import {useEventStore} from "../../../../../store/event";
import {useParticipationStore} from "../../../../../store/participation";
import {downloadIcon} from "@spoferan/nuxt-spoferan/icons";

const eventStore = useEventStore();
const participationStore = useParticipationStore();
const {$web, $date} = useNuxtApp();
const {t} = useI18n();

// Make sure the page is activated
const page = eventStore.getPage(EventPageType.Map);
if (!page) {
	await navigateTo($web('events.participations.show', [eventStore.event._key, participationStore.participation._key]));
}

const {data: {tracks, trackSpots, checkpoints, page: pageStyle}} = await useApiFetch(`/v1/view/events/${eventStore.event.id}/participation-classes/${participationStore.participation.participationClass.id}/map`, {
	guest: true,
	params: {
		ts: eventStore.event.map_cache_buster + '_' + (page ? page.cache_buster : ''),
	}
});

const icons = {
	download: downloadIcon,
}

const selectedChartDistanceFromStart = ref(0);
const chartTracks = computed(() => {
	return tracks.filter(track => track.for_participation && track.latlngs.some(latLng => !!latLng.ele));
});
const selectedChartLatLngPerTrack = computed(() => {
	const charts = {};
	chartTracks.value.forEach(track => {
		const latLng = track.latlngs.find(latLng => latLng.distance_from_start >= selectedChartDistanceFromStart.value);
		if (latLng) {
			charts[track.id] = latLng;
		}
	});
	return charts;
});

const selectedDownloadTrackId = ref(undefined);
const showTrackDownloadFormModal = ref(false);

const focusedTrack = ref(null);

function downloadTrack(track: undefined|object = undefined) {
	selectedDownloadTrackId.value = track ? track.id : undefined;
	nextTick(function () {
		showTrackDownloadFormModal.value = true;
	});
}

useMeta({
	title: eventStore.buildPageTitle(t('view.events.map.index.title', {
		event: eventStore.event.display_name,
	})) as string,
	description: t('view.events.map.index.description', {
		event: eventStore.event.display_name,
	}) as string,
	noIndex: true
})

</script>

